import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";
import { useSearchParams, Link } from "react-router-dom";
import { List, Map as MapIcon, SlidersHorizontal, Info, HandHeart } from "lucide-react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import SendRequestModal from "@/components/SendRequestModal";
import SearchForm from "@/components/SearchForm";
import ListingCard from "@/components/ListingCard";
import SmartFilterBar from "@/components/SmartFilterBar";
import ResultsMap from "@/components/ResultsMap";
import SaveSearchButton from "@/components/SaveSearchButton";
import useRequestsConfig from "@/lib/useRequestsConfig";
import Seo from "@/seo/Seo";
import { PausedCategoryNotice } from "@/components/PausedCategoryNotice";
import { DISABLED_FLOORING } from "@/lib/api";

const EMPTY_FILTER = { colour: "", brand: "", condition: "", maxPrice: 0 };

export default function Search() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [showRequest, setShowRequest] = useState(false);
  const { user } = useAuth();
  const { unit } = useUnit();
  // Return the shopper straight to the request review step after they sign in.
  useEffect(() => {
    if (!user || user.role !== "customer" || user.email_verified === false) return;
    try {
      if (sessionStorage.getItem("fm_request_intent")) {
        sessionStorage.removeItem("fm_request_intent");
        setShowRequest(true);
      }
    } catch (e) { /* ignore */ }
  }, [user]);
  const requestsCfg = useRequestsConfig();

  const initial = {
    room_width: params.get("room_width") || "",
    room_length: params.get("room_length") || "",
    postcode: params.get("postcode") || "",
    radius_km: params.get("radius_km") || "25",
    flooring_type: params.get("flooring_type") || "",
    max_price: params.get("max_price") || "",
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setFilter(EMPTY_FILTER); // a fresh search clears the on-page filters
    api
      .get(`/listings/search?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Search failed"))
      .finally(() => setLoading(false));
  }, [params]);

  const filtered = useMemo(() => {
    const items = data?.results || [];
    return items.filter((l) => {
      if (filter.colour && (l.colour || "").toLowerCase() !== filter.colour) return false;
      if (filter.brand && l.manufacturer !== filter.brand) return false;
      if (filter.condition && l.condition !== filter.condition) return false;
      if (filter.maxPrice && (l.price_gbp || 0) > filter.maxPrice) return false;
      return true;
    });
  }, [data, filter]);

  const filtersActive = ["colour", "brand", "condition", "maxPrice"].some((k) => filter[k]);

  // "Ask retailers" nudge — only when we have a complete room + postcode and
  // the search returns few/no matches (a natural "can't find it?" moment).
  const canRequest = Boolean(initial.room_width && initial.room_length && initial.flooring_type && initial.postcode);
  const showRequestNudge = requestsCfg.enabled && !loading && !error && canRequest && (data?.count ?? 0) <= 2;
  const requestRoom = {
    name: "Room",
    width_m: Number(initial.room_width),
    length_m: Number(initial.room_length),
    flooring_type: initial.flooring_type,
    colour: filter.colour || null,
  };

  return (
    <>
      <Seo
        title={
          initial.room_width && initial.room_length
            ? `Flooring for ${dimPairLabel(Number(initial.room_width), Number(initial.room_length), unit)} rooms — Greater Manchester`
            : "Browse surplus flooring — Greater Manchester"
        }
        description="Search live surplus flooring in Greater Manchester. Filter by room size, postcode, category and budget. Carpet, laminate, LVT and vinyl — matched, reserved, collected locally."
        path="/search"
        robots={
          // Search results with query strings shouldn't be crawled — canonical
          // is the base /search page. Prevents duplicate-URL dilution.
          Object.keys(Object.fromEntries(params)).length > 0
            ? "noindex, follow"
            : undefined
        }
      />
      <PublicHeader />
      <main className="min-h-screen" data-testid="search-page">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h1 className="font-display font-semibold text-2xl sm:text-3xl text-slate-900">
              {initial.room_width && initial.room_length
                ? `Flooring that fits ${dimPairLabel(Number(initial.room_width), Number(initial.room_length), unit)}`
                : "Browse remnants"}
            </h1>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#E05915] transition-colors"
              data-testid="toggle-filters"
            >
              <SlidersHorizontal className="w-4 h-4" aria-hidden="true" />
              {showFilters ? "Hide filters" : "Edit search"}
            </button>
          </div>
          {(showFilters || !data) && <SearchForm compact initial={initial} />}
          <Link
            to="/whole-home"
            className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#2A5A3B]/25 bg-[#2A5A3B]/5 px-4 py-3 hover:bg-[#2A5A3B]/10 transition-colors"
            data-testid="search-whole-home-link"
          >
            <span className="text-sm text-slate-700"><strong>Flooring a whole property?</strong> Match several rooms at once and see the total saving.</span>
            <span className="text-sm font-semibold text-[#2A5A3B] whitespace-nowrap">Floor several rooms →</span>
          </Link>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-2 pb-16">
          {!loading && !error && data?.count > 0 && (
            <SmartFilterBar listings={data.results} value={filter} onChange={setFilter} />
          )}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="text-sm text-slate-600" data-testid="results-count">
              {loading
                ? "Searching…"
                : error
                ? "There was a problem searching."
                : `${filtersActive ? `${filtered.length} of ${data?.count || 0}` : data?.count || 0} matching ${(filtersActive ? filtered.length : (data?.count || 0)) === 1 ? "listing" : "listings"}${
                    initial.postcode ? ` within ${Math.round(Number(initial.radius_km) / 1.60934)} miles of ${initial.postcode}` : ""
                  }`}
            </div>
            <div className="flex items-center gap-2">
              <SaveSearchButton
                compact
                filters={{
                  room_width_m: initial.room_width ? Number(initial.room_width) : undefined,
                  room_length_m: initial.room_length ? Number(initial.room_length) : undefined,
                  postcode: initial.postcode || undefined,
                  radius_km: initial.radius_km ? Number(initial.radius_km) : 25,
                  flooring_type: initial.flooring_type || undefined,
                  max_price: initial.max_price ? Number(initial.max_price) : undefined,
                }}
              />
              <div className="inline-flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setView("list")}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    view === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
                  }`}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" aria-hidden="true" /> List
                </button>
                <button
                  onClick={() => setView("map")}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    view === "map" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
                  }`}
                  data-testid="view-map-btn"
                >
                  <MapIcon className="w-4 h-4" aria-hidden="true" /> Map
                </button>
              </div>
            </div>
          </div>

          {DISABLED_FLOORING.has(initial.flooring_type) && <div className="mb-4"><PausedCategoryNotice /></div>}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
              {error}
            </div>
          )}

          {!loading && !error && data?.count === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-600">
              <Info className="w-6 h-6 text-slate-400 mx-auto mb-2" aria-hidden="true" />
              No remnants match your room right now. Try increasing your distance radius, removing
              the flooring type filter, or checking back tomorrow — new roll ends are added daily.
              <div className="mt-4">
                <Link to="/search" className="fm-btn-primary inline-block" data-testid="clear-filters-btn">
                  Show all remnants
                </Link>
              </div>
            </div>
          )}

          {!loading && !error && data?.count > 0 && filtered.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-600" data-testid="filtered-empty">
              <Info className="w-6 h-6 text-slate-400 mx-auto mb-2" aria-hidden="true" />
              No remnants match those filters. Try loosening the colour, brand, condition or budget.
              <div className="mt-4">
                <button
                  onClick={() => setFilter(EMPTY_FILTER)}
                  className="fm-btn-primary inline-block"
                  data-testid="reset-onpage-filters-btn"
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && view === "list" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="results-grid">
              {filtered.map((l) => (
                <ListingCard key={l.id} l={l} />
              ))}
            </div>
          )}

          {!loading && !error && filtered.length > 0 && view === "map" && (
            <ResultsMap items={filtered} origin={data.origin} />
          )}

          {showRequestNudge && (
            <div className="mt-6 rounded-2xl border border-[#2A5A3B]/25 bg-[#2A5A3B]/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="search-request-nudge">
              <div className="flex-1">
                <h2 className="font-display font-semibold text-lg text-slate-900">
                  {data?.count === 0 ? "Can't find it? Ask nearby retailers" : "Not quite right? Ask nearby retailers"}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Tell local retailers what you're looking for and they'll let you know when matching stock comes in. Your details stay private.
                </p>
              </div>
              <button
                onClick={() => setShowRequest(true)}
                className="shrink-0 h-12 px-6 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="search-send-request-btn"
              >
                <HandHeart className="w-4 h-4" /> Ask retailers
              </button>
            </div>
          )}
        </section>
      </main>

      {showRequest && (
        <SendRequestModal
          rooms={[requestRoom]}
          postcode={initial.postcode}
          source="search"
          onClose={() => setShowRequest(false)}
        />
      )}
    </>
  );
}
